import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import NotificationDropdown from './NotificationDropdown';
import { useNotifications } from '../context/NotificationContext';
import { useUser } from '../context/UserContext';
import friendService from '../services/friendService';
import discoBall from '../assets/img/disco-ball.svg';
import LottieAnimation from './LottieAnimation';
import mirrorBallData from '../assets/lottie/mirror-ball-2.json';

// Friends lottie animations
import karaokeData from '../assets/lottie/friends/karaoke.json';
import birthdaySurpriseData from '../assets/lottie/friends/birthday-surprise.json';
import hostingPodcastData from '../assets/lottie/friends/hosting-podcast.json';
import travelBuddiesData from '../assets/lottie/friends/travel-buddies.json';
import birthdayCelebrationData from '../assets/lottie/friends/friends-birthday-celebration.json';
import volleyballData from '../assets/lottie/friends/friends-playing-volleyball.json';
import singingAtPartyData from '../assets/lottie/friends/singing-at-party.json';
import karaokeGroupData from '../assets/lottie/friends/people-doing-karaoke.json';
import confettiData from '../assets/lottie/friends/boy-with-confetti.json';
import wineGlassData from '../assets/lottie/friends/girl-holding-wine-glass.json';

const friendsLotties = [
  karaokeData, birthdaySurpriseData, hostingPodcastData, travelBuddiesData,
  birthdayCelebrationData, volleyballData, singingAtPartyData, karaokeGroupData,
  confettiData, wineGlassData,
];

/**
 * Navigation bar component with mode switch and logout
 * Design: High-contrast, hard edges, monospace
 */
export default function Navbar({
  mode = 'guest', // 'guest', 'host'
  onModeChange,
  user,
  walletBalance,
  onLogout,
  onLogoClick,
  className = '',
}) {
  const navigate = useNavigate();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [friendRequestCount, setFriendRequestCount] = useState(0);
  const [friendsIndexA, setFriendsIndexA] = useState(() => Math.floor(Math.random() * friendsLotties.length));
  const [friendsIndexB, setFriendsIndexB] = useState(() => Math.floor(Math.random() * friendsLotties.length));
  const [friendsShowA, setFriendsShowA] = useState(true);
  const friendsActiveRef = useRef({ index: friendsIndexA, showA: true });
  const userDropdownRef = useRef(null);
  const notifDropdownRef = useRef(null);

  let unreadCount = 0;
  try {
    const notif = useNotifications();
    unreadCount = notif.unreadCount;
  } catch {
    // NotificationProvider not available (unauthenticated routes)
  }

  let escrowBalance = 0;
  try {
    const userCtx = useUser();
    escrowBalance = userCtx.escrowBalance || 0;
  } catch {
    // UserProvider not available
  }

  // Fetch pending friend request count
  useEffect(() => {
    if (!user) return;
    async function fetchCount() {
      const result = await friendService.getPendingRequests();
      if (result.success) {
        setFriendRequestCount((result.data || []).length);
      }
    }
    fetchCount();
  }, [user]);

  // Rotate friends lottie every 9 seconds with crossfade (A/B layers)
  useEffect(() => {
    const interval = setInterval(() => {
      const { index: current, showA } = friendsActiveRef.current;
      let next;
      do { next = Math.floor(Math.random() * friendsLotties.length); } while (next === current && friendsLotties.length > 1);
      // Load new animation into the hidden layer, then fade to it
      if (showA) {
        setFriendsIndexB(next);
        requestAnimationFrame(() => setFriendsShowA(false));
      } else {
        setFriendsIndexA(next);
        requestAnimationFrame(() => setFriendsShowA(true));
      }
      friendsActiveRef.current = { index: next, showA: !showA };
    }, 9000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdowns on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (notifDropdownRef.current && !notifDropdownRef.current.contains(event.target)) {
        setShowNotifDropdown(false);
      }
    }
    if (showUserDropdown || showNotifDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDropdown, showNotifDropdown]);

  const handleLogoClick = () => {
    if (!user) {
      onLogoClick?.();
      return;
    }
    navigate(mode === 'host' ? '/host' : '/guest');
  };

  const handleNotifDiscoClick = () => {
    if (window.innerWidth < 640) {
      navigate('/notifications');
      return;
    }
    setShowNotifDropdown(prev => !prev);
    setShowUserDropdown(false);
  };

  const handleFriendIconClick = () => {
    navigate('/friends');
  };

  const handleUserDropdownToggle = () => {
    setShowUserDropdown(prev => !prev);
    setShowNotifDropdown(false);
  };

  return (
    <nav className={`w-full border-b-2 border-black bg-white ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo — disco ball + "PARTY RESUME" as home link */}
          <button
            type="button"
            onClick={handleLogoClick}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            {/* <img src={discoBall} alt="Disco ball" className="w-16 h-16" /> */}
            <LottieAnimation animationData={mirrorBallData} width={48} height={48} />
            <span className="font-mono text-lg font-bold uppercase tracking-wider text-black hidden sm:block">
              PARTY RESUME
            </span>
          </button>

          {/* Center - Mode Switch */}
          {onModeChange && (
            <div className="hidden md:flex items-center">
              <ModeSwitch mode={mode} onChange={onModeChange} />
            </div>
          )}

          {/* Right side - User menu */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                {/* Party Coin Balance - Desktop */}
                {walletBalance !== null && walletBalance !== undefined && (
                  <button
                    onClick={() => navigate('/wallet')}
                    className="hidden sm:flex flex-col items-end hover:opacity-70 transition-opacity"
                  >
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-lg font-bold text-black">
                        {Math.floor(walletBalance / 100)}
                      </span>
                      <span className="text-lg">🪙</span>
                    </div>
                    {escrowBalance > 0 && (
                      <span className="font-mono text-[10px] text-gray-400 leading-none">
                        +{Math.floor(escrowBalance / 100)} escrow
                      </span>
                    )}
                  </button>
                )}

                {/* Party Coin Balance - Mobile */}
                {walletBalance !== null && walletBalance !== undefined && (
                  <button
                    onClick={() => navigate('/wallet')}
                    className="flex sm:hidden items-center gap-1 hover:opacity-70 transition-opacity"
                  >
                    <span className="font-mono text-sm font-bold text-black">
                      {Math.floor(walletBalance / 100)}
                    </span>
                    <span className="text-sm">🪙</span>
                  </button>
                )}

                {/* Friend Requests Icon */}
                <button
                  type="button"
                  onClick={handleFriendIconClick}
                  className="relative hover:opacity-80 transition-opacity"
                >
                  <div className="relative" style={{ width: 72, height: 72 }}>
                    <div className="absolute inset-0 transition-opacity duration-700" style={{ opacity: friendsShowA ? 1 : 0 }}>
                      <LottieAnimation animationData={friendsLotties[friendsIndexA]} width={72} height={72} />
                    </div>
                    <div className="absolute inset-0 transition-opacity duration-700" style={{ opacity: friendsShowA ? 0 : 1 }}>
                      <LottieAnimation animationData={friendsLotties[friendsIndexB]} width={72} height={72} />
                    </div>
                  </div>
                  {friendRequestCount > 0 && (
                    <span className="absolute -right-1 min-w-[16px] h-4 flex items-center justify-center bg-black text-white font-mono text-[9px] font-bold px-0.5 border border-white" style={{ top: 13 }}>
                      {friendRequestCount > 99 ? '99+' : friendRequestCount}
                    </span>
                  )}
                </button>

                {/* User dropdown trigger */}
                <div className="relative" ref={userDropdownRef}>
                  <button
                    type="button"
                    onClick={handleUserDropdownToggle}
                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                  >
                    {/* Avatar */}
                    <div className="w-8 h-8 border-2 border-black overflow-hidden flex items-center justify-center bg-gray-100 flex-shrink-0">
                      {user.avatarUrl ? (
                        <img
                          src={user.avatarUrl}
                          alt={user.displayName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="font-mono text-sm font-bold text-black">
                          {user.displayName?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      )}
                    </div>
                    {/* Username - desktop only */}
                    <span className="hidden sm:block font-mono text-sm font-semibold uppercase tracking-wider text-black">
                      {user.displayName}
                    </span>
                    {/* Chevron */}
                    <svg className="w-4 h-4 text-black flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* User dropdown menu */}
                  {showUserDropdown && (
                    <div className="absolute top-full right-0 mt-2 w-52 border-2 border-black bg-white z-50 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                      <button
                        type="button"
                        onClick={() => {
                          setShowUserDropdown(false);
                          navigate(`/users/${user?.userId}`);
                        }}
                        className="w-full text-left px-4 py-3 font-mono text-xs font-bold uppercase tracking-wider hover:bg-gray-50 transition-colors"
                      >
                        VIEW PARTY RESUME
                      </button>
                      <div className="border-t-2 border-black" />
                      <button
                        type="button"
                        onClick={() => {
                          setShowUserDropdown(false);
                          navigate('/credential-builder');
                        }}
                        className="w-full text-left px-4 py-3 font-mono text-xs font-bold uppercase tracking-wider hover:bg-gray-50 transition-colors"
                      >
                        EDIT PARTY RESUME
                      </button>
                      <div className="border-t-2 border-black" />
                      <button
                        type="button"
                        onClick={() => {
                          setShowUserDropdown(false);
                          onLogout();
                        }}
                        className="w-full text-left px-4 py-3 font-mono text-xs font-bold uppercase tracking-wider hover:bg-gray-50 transition-colors"
                      >
                        LOGOUT
                      </button>
                    </div>
                  )}
                </div>

                {/* Small disco ball for notifications */}
                <div className="relative" ref={notifDropdownRef}>
                  <button
                    type="button"
                    onClick={handleNotifDiscoClick}
                    className="relative hover:opacity-80 transition-opacity"
                  >
                    <img src={discoBall} alt="Notifications" className="w-8 h-8" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-4 flex items-center justify-center bg-black text-white font-mono text-[9px] font-bold px-0.5 border border-white">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                  {showNotifDropdown && (
                    <NotificationDropdown onClose={() => setShowNotifDropdown(false)} />
                  )}
                </div>
              </>
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={onLogoClick}
              >
                SIGN IN
              </Button>
            )}
          </div>
        </div>

        {/* Mobile mode switch */}
        {onModeChange && (
          <div className="md:hidden pb-4">
            <ModeSwitch mode={mode} onChange={onModeChange} fullWidth />
          </div>
        )}
      </div>
    </nav>
  );
}

/**
 * Mode switch component (Find Your Party / Find Your People toggle)
 */
export function ModeSwitch({
  mode,
  onChange,
  fullWidth = false,
  className = '',
}) {
  return (
    <div className={`inline-flex border-2 border-black ${fullWidth ? 'w-full' : ''} ${className}`}>
      <button
        type="button"
        onClick={() => onChange('guest')}
        className={`
          flex-1 px-6 py-2
          font-mono text-sm font-semibold uppercase tracking-wider
          transition-colors duration-200
          ${mode === 'guest'
            ? 'bg-black text-white'
            : 'bg-white text-black hover:bg-gray-100'
          }
        `}
      >
        GUEST
      </button>
      <button
        type="button"
        onClick={() => onChange('host')}
        className={`
          flex-1 px-6 py-2
          border-l-2 border-black
          font-mono text-sm font-semibold uppercase tracking-wider
          transition-colors duration-200
          ${mode === 'host'
            ? 'bg-black text-white'
            : 'bg-white text-black hover:bg-gray-100'
          }
        `}
      >
        HOST
      </button>
    </div>
  );
}

/**
 * Breadcrumb navigation component
 */
export function Breadcrumb({
  items,
  className = '',
}) {
  return (
    <nav className={`flex items-center gap-2 ${className}`}>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <span className="font-mono text-gray-400">/</span>
          )}
          {item.href || item.onClick ? (
            <button
              type="button"
              onClick={item.onClick}
              className="font-mono text-sm uppercase tracking-wider text-gray-600 hover:text-black transition-colors"
            >
              {item.label}
            </button>
          ) : (
            <span className="font-mono text-sm uppercase tracking-wider text-black font-semibold">
              {item.label}
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

/**
 * Page header component
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  breadcrumbs,
  className = '',
}) {
  return (
    <div className={`border-b-2 border-black bg-white px-6 py-6 ${className}`}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumb items={breadcrumbs} className="mb-4" />
      )}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-mono text-2xl font-bold uppercase tracking-wider text-black">
            {title}
          </h1>
          {subtitle && (
            <p className="font-mono text-sm text-gray-600 mt-1">
              {subtitle}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-3 flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
